import { AuditSchemaBuilder } from '@ifs-gcp/core';
import type { AuditModules, AuditType, ChecklistChild } from '@ifs-gcp/core/models';

import allChecklists from '@/assets/definitions/checklists/all.json';
import allTemplates from '@/assets/definitions/templates/all.json';

type TemplateKey = keyof typeof allTemplates;

const entityDefinitionMap = {
  checklists: allChecklists,
  templates: allTemplates,
};

const getDefinitionVersion = (type: string, id: string): string | null => {
  const entityMap = entityDefinitionMap[type as keyof typeof entityDefinitionMap];

  if (!entityMap) {
    return null;
  }

  const entityEntry = entityMap?.[id as keyof typeof entityMap] as { current?: string };

  const version = entityEntry?.current;

  if (!version) {
    return null;
  }

  return version;
};

const fetchJson = async (path: string): Promise<unknown> => {
  const result = await fetch(`/assets/definitions/${path}?v=${window.MODULE_DEFINITION_VERSION}`);

  if (!result.ok) {
    throw new Error(`Failed to load JSON at ${path}: ${result.status} ${result.statusText}`);
  }

  return result.json();
};

export const ifsLoaders = {
  forEntity: async <T = unknown>(type: string, id: string): Promise<T> => {
    const version = getDefinitionVersion(type, id);

    if (!version) {
      throw new Error(`No version found for entity: ${type}/${id}`);
    }

    return fetchJson(`${type}/${id}/${version}.json`) as Promise<T>;
  },
  forPath: (path: string): Promise<unknown> => fetchJson(path),
};

export const loadJsonChecklist = (key: string): Promise<ChecklistChild['props']> =>
  ifsLoaders.forEntity<ChecklistChild['props']>('checklists', key);

export const loadJsonTemplate = <T>(key: TemplateKey): Promise<T> =>
  ifsLoaders.forEntity<T>('templates', key);

export const getAssetFile = <T>(assetPath: string): Promise<T> =>
  ifsLoaders.forPath(`assets/${assetPath}`) as Promise<T>;

export const getSchemaBuilder = (templatesMap: Record<AuditType, string>, modules: AuditModules) =>
  new AuditSchemaBuilder(ifsLoaders, templatesMap, modules);
